import { memo } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AttachmentRow } from '@/components/common/AttachmentRow';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { NoteAttachment } from '@/types/notes';

import styles from '../NoteDetailPanel.module.css';

interface NoteAttachmentListProps {
  attachments: readonly NoteAttachment[];
  onPreview: (index: number) => void;
  onDownload: (attachment: NoteAttachment) => void;
}

/**
 * Read-only attachment list for note detail panels (authenticated and public).
 * Pure presentation — download/preview behavior is injected by the caller, so
 * the same list works against the authenticated and the public share-token
 * endpoints.
 */
export const NoteAttachmentList = memo(function NoteAttachmentList({
  attachments,
  onPreview,
  onDownload,
}: NoteAttachmentListProps) {
  const { t } = useTranslation();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className={styles.attachmentGroup}>
      {attachments.map((attachment, index) => (
        <AttachmentRow
          key={attachment.id}
          name={attachment.fileName}
          size={attachment.fileSize}
          date={attachment.createdAt}
          mimeType={attachment.contentType}
          onClick={() => onPreview(index)}
          actions={
            <Tooltip label={t('filePreview.download')}>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => onDownload(attachment)}
              >
                <IconDownload size={ICON_SIZE_SM} />
              </ActionIcon>
            </Tooltip>
          }
        />
      ))}
    </div>
  );
});
